import { fetchAPI } from '@/lib/api';

/**
 * 모집 상태: 백엔드 `RecruitmentStatus` 와 1:1.
 *
 * 계획: docs/plans/2026-08-18/게스트-신청-에디터-화면.md Phase 2.
 */
export type RecruitmentStatus = 'UPCOMING' | 'OPEN' | 'FULL' | 'EXPIRED';

export type RecruitingProgramType = 'MENTORING' | 'PROJECT' | 'INTERNSHIP' | 'CUSTOM';

/**
 * 이 프로그램은 팀을 언제·누가 만드는가 — 신청 접수 방식.
 *
 * 계획: docs/plans/2026-08-20/프로그램별-신청접수-방식-계획.md §2
 *
 * 접수 방식은 프로그램마다 다르고 하나로 만들 수 없다. 값이 없거나 알 수 없으면
 * `NONE` 이다 — 팀 기능은 담당자가 켜야 켜진다.
 */
export type TeamFormation = 'NONE' | 'APPLICANT_LED' | 'STAFF_ASSIGNED';

/**
 * 이 신청 서식은 누구용인가. 담당자가 명시 지정하며, 서식 제목으로 추측하지 않는다.
 *
 * 미지정은 `null` 이다 — "지정하지 않았다" 와 "단독이다" 는 다르다.
 */
export type ApplicantRole = 'SOLO' | 'LEAD' | 'MEMBER';

/**
 * 카드 안의 신청 서식 한 줄 = 버튼 하나.
 *
 * 마감·정원이 서식마다 다르므로(멘토용 20팀 / 멘티용 무제한) 상태도 줄마다 따로 온다.
 */
export interface RecruitingProgramForm {
  publicLinkId: string;
  /** 서식 제목. 그대로 버튼 라벨이 된다. */
  title: string;
  status: RecruitmentStatus;
  deadline?: string | null;
  /** 대표용·팀원용·단독. 담당자가 지정하지 않았으면 null. */
  applicantRole?: ApplicantRole | null;
}

/**
 * 랜딩 "신청 가능한 프로그램" 카드 한 장.
 *
 * 비인증 응답이라 필드가 최소화되어 있다: 신청자 수·명단·담당자는 오지 않는다.
 *
 * 카드 하나 = 프로그램 하나. 신청 서식이 여럿이면 카드를 쪼개지 않고 `forms` 가 늘어난다.
 */
export interface RecruitingProgram {
  programId: string;
  name: string;
  type: RecruitingProgramType;
  note?: string | null;
  /** 카드 배지에 쓰는 대표 상태 — 서식 중 하나라도 열려 있으면 OPEN. */
  status: RecruitmentStatus;
  openFrom?: string | null;
  /** 베타 기간 샘플 모집: 신청 서식이 아직 실제 신청서가 아니다. 카드에 표시한다. */
  sample?: boolean;
  /** 신청 서식 목록. 서버는 빈 카드를 만들지 않으므로 항상 1개 이상이다. */
  forms: RecruitingProgramForm[];
}

/** 모집 중인 프로그램 목록 (비인증). 마감 임박순. */
export async function listRecruitingPrograms(): Promise<RecruitingProgram[]> {
  return fetchAPI('/api/public/recruiting-programs');
}

/** 신청 서식 한 줄 — 어떤 공개링크를, 누구용으로 여는가. */
export interface RecruitmentFormSetting {
  publicLinkId: string;
  /**
   * 이 서식이 누구용인가. `null` 은 담당자가 아직 고르지 않았다는 뜻이고,
   * 화면은 그 상태를 "역할 미지정" 으로 보여준다. 제목으로 추측해 채우지 않는다.
   */
  applicantRole: ApplicantRole | null;
}

/** 프로그램 정책에 저장되는 모집 설정. 마감·정원은 여기 두지 않는다 (공개링크가 정본). */
export interface RecruitmentSettings {
  enabled: boolean;
  /** 신청 서식 목록. 저장 순서가 카드의 버튼 순서가 된다. */
  forms: RecruitmentFormSetting[];
  /**
   * 접수 방식. 없으면 `NONE` — 팀을 만들지 않는다.
   *
   * 팀 크기·팀 수·역할 구성에 **제한을 걸지 않는다** (2026-08-20 확정 결정).
   * 이 값이 정하는 것은 팀이 언제 생기느냐뿐이다.
   */
  teamFormation: TeamFormation;
  openFrom?: string | null;
  note?: string | null;
  /**
   * 베타 기간 샘플 표시. 담당자 UI 에는 편집 수단이 없고(운영자가 DB 에서 켠다),
   * 저장 때 사라지지 않도록 읽기·쓰기 양쪽에서 그대로 실어 나른다.
   */
  sample?: boolean;
}

const APPLICANT_ROLES: ApplicantRole[] = ['SOLO', 'LEAD', 'MEMBER'];
const TEAM_FORMATIONS: TeamFormation[] = ['NONE', 'APPLICANT_LED', 'STAFF_ASSIGNED'];

function readApplicantRole(raw: unknown): ApplicantRole | null {
  return typeof raw === 'string' && (APPLICANT_ROLES as string[]).includes(raw)
    ? (raw as ApplicantRole)
    : null;
}

/** 알 수 없는 값은 NONE 이다 — 설정이 깨졌다고 팀 화면이 열리면 안 된다. */
function readTeamFormation(raw: unknown): TeamFormation {
  return typeof raw === 'string' && (TEAM_FORMATIONS as string[]).includes(raw)
    ? (raw as TeamFormation)
    : 'NONE';
}

/**
 * 신청 서식 목록을 읽는다. 키 셋을 **우선순위대로 하나만** 본다 — 여럿을 읽어 합치면
 * 담당자가 뺀 서식이 되살아난다. 백엔드 `ProgramRecruitmentService.readForms` 와 같은 규칙이다.
 *
 * 1. `forms`: 역할까지 지정된 정본
 * 2. `publicLinkIds`: 역할 도입(2026-08-20) 이전. **역할은 비운다**
 * 3. `publicLinkId`: 복수화(2026-08-19) 이전. 역할은 비운다
 *
 * 2·3 에서 역할을 제목으로 추측해 채우지 않는다 — 담당자가 지정한 사실이 아니다.
 */
function readForms(value: Record<string, unknown>): RecruitmentFormSetting[] {
  const seen = new Set<string>();
  const push = (
    list: RecruitmentFormSetting[],
    publicLinkId: string,
    applicantRole: ApplicantRole | null,
  ) => {
    if (!publicLinkId || seen.has(publicLinkId)) return;
    seen.add(publicLinkId);
    list.push({ publicLinkId, applicantRole });
  };

  if (Array.isArray(value.forms)) {
    const forms: RecruitmentFormSetting[] = [];
    for (const entry of value.forms) {
      if (!entry || typeof entry !== 'object') continue;
      const row = entry as Record<string, unknown>;
      if (typeof row.publicLinkId !== 'string') continue;
      push(forms, row.publicLinkId, readApplicantRole(row.applicantRole));
    }
    return forms;
  }

  if (Array.isArray(value.publicLinkIds)) {
    const forms: RecruitmentFormSetting[] = [];
    for (const id of value.publicLinkIds) {
      if (typeof id === 'string') push(forms, id, null);
    }
    return forms;
  }

  return typeof value.publicLinkId === 'string' && value.publicLinkId
    ? [{ publicLinkId: value.publicLinkId, applicantRole: null }]
    : [];
}

/** 프로그램 policies 에서 모집 설정을 읽는다. 없으면 비활성 기본값. */
export function readRecruitmentSettings(
  policies?: Record<string, unknown> | null,
): RecruitmentSettings {
  const raw = policies?.recruitment;
  if (!raw || typeof raw !== 'object') {
    return { enabled: false, forms: [], teamFormation: 'NONE' };
  }
  const value = raw as Record<string, unknown>;
  return {
    enabled: value.enabled === true,
    forms: readForms(value),
    teamFormation: readTeamFormation(value.teamFormation),
    openFrom: typeof value.openFrom === 'string' ? value.openFrom : null,
    note: typeof value.note === 'string' ? value.note : null,
    sample: value.sample === true,
  };
}

/**
 * 기존 policies 를 보존하며 모집 설정만 갈아끼운다.
 *
 * updateProgram 은 policies 객체를 통째로 교체하므로, 다른 정책(roster 공개 등)을
 * 함께 보내지 않으면 조용히 사라진다.
 */
export function withRecruitmentSettings(
  policies: Record<string, unknown> | null | undefined,
  settings: RecruitmentSettings,
): Record<string, unknown> {
  return {
    ...(policies ?? {}),
    recruitment: {
      enabled: settings.enabled,
      teamFormation: settings.teamFormation ?? 'NONE',
      // `forms` 만 쓴다. 옛 `publicLinkIds`·`publicLinkId` 는 읽기 호환일 뿐이라 다시
      // 쓰지 않는다 — 두 키를 함께 저장하면 어느 쪽이 정본인지 곧 갈라진다.
      forms: (settings.forms ?? []).map((form) => ({
        publicLinkId: form.publicLinkId,
        applicantRole: form.applicantRole ?? null,
      })),
      openFrom: settings.openFrom || null,
      note: settings.note || null,
      // 담당자 화면에 편집 수단이 없는 값이라, 여기서 빠뜨리면 저장 한 번에 조용히 꺼진다.
      sample: settings.sample === true,
    },
  };
}
